import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { COMPETENCY_LEVEL_LABELS, type CompetencyLevel } from '../../../human-resources/personnel/personnelTypes';
import { surveyRespondentsApi } from '../../survey/surveyApi';
import type { DevelopmentPlanDetail } from '../developmentPlanTypes';

const ID = 'impact-assessment';

/**
 * The ladder's RATED rungs, in its own order — `sort="none"` keeps it so. Not Rated is left out:
 * an assessment that rates nothing has assessed nothing, and the server refuses it.
 */
const REACHED_LEVELS = ([1, 2, 3, 4] as CompetencyLevel[]).map((l) => ({
  value: String(l),
  label: COMPETENCY_LEVEL_LABELS[l],
}));

/**
 * Create impact assessment — or edit it — once the employee's training report is in (product call,
 * 12 Oct 2026): for EVERY development need on the plan, the level the employee reached, on the same
 * ladder as the level before and the target; and remarks. Revisable; the employee reads it.
 *
 * One write, on `surveys.assess-impact` — Survey's own endpoint, since the plan and its needs are
 * Survey's. Each need's "before" and "target" are STATED beside the control, because the level
 * reached is a judgement made against them.
 *
 * NO Autogenerate: the level somebody reached and what a training changed are an assessor's
 * judgement — generating one would put a finding in their mouth.
 */
export function ImpactAssessmentModal({
  detail,
  employeeName,
  onClose,
  onSaved,
}: {
  detail: DevelopmentPlanDetail;
  employeeName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const plan = detail.summary;
  // `!= null`: it came off the wire, where an absent value is undefined.
  const editing = plan.impactAssessmentAt != null;

  // The level chosen per need id — '' while none is; seeded from what is stored on an edit.
  const [levels, setLevels] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      detail.developmentNeeds.map((n) => [n.id, n.achievedLevel != null ? String(n.achievedLevel) : '']),
    ),
  );
  const [remarks, setRemarks] = useState(plan.impactAssessmentRemarks ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const levelsComplete = detail.developmentNeeds.every((n) => (levels[n.id] ?? '') !== '');
  const valid = levelsComplete && remarks.trim() !== '';
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await surveyRespondentsApi.saveImpactAssessment(plan.surveyId, plan.id, {
        needs: detail.developmentNeeds.map((n) => ({
          needId: n.id,
          achievedLevel: Number(levels[n.id]) as CompetencyLevel,
        })),
        remarks: remarks.trim(),
      });

      toast.success(`Saved ${employeeName}'s impact assessment.`);
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('The impact assessment could not be saved.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit impact assessment' : 'Create impact assessment'}
      size="xl"
      id={`${ID}-modal`}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {detail.developmentNeeds.length === 0 ? (
          // No needs were identified on this plan — the assessment is its remarks alone.
          <p className="text-xs text-slate-600">
            This plan identified no development needs, so there is no level to record — only the remarks.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {detail.developmentNeeds.map((need) => (
              <Select
                key={need.id}
                id={`${ID}-level-${need.id}`}
                label={need.competencyName}
                required
                // The judgement is made AGAINST these two, so they are said where it is made.
                description={`The level reached after the training. Before: ${COMPETENCY_LEVEL_LABELS[need.currentLevel]}. Target: ${COMPETENCY_LEVEL_LABELS[need.targetLevel]}.`}
                placeholder="Level reached"
                value={levels[need.id] || null}
                onChange={(next) => setLevels((all) => ({ ...all, [need.id]: next }))}
                options={REACHED_LEVELS}
                sort="none"
                searchable={false}
                clearable={false}
                error={(levels[need.id] ?? '') === '' ? 'Choose the level reached.' : undefined}
                disabled={saving}
              />
            ))}
          </div>
        )}

        <Textarea
          id={`${ID}-remarks`}
          label="Remarks"
          required
          maxLength={2000}
          description="What the training changed, and what should follow. The employee reads this on their own survey."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={remarks.trim() ? error?.fieldError('remarks') : 'Say what the training changed.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
